import React from "react";
import { Truck } from "lucide-react";

/**
 * Thin marquee bar — seamless right→left scroll, visible on every page.
 *
 * Usage:
 *   <ShippingMarquee />                              // default free-shipping line
 *   <ShippingMarquee message="🎉 BLACK FRIDAY — 25% OFF SITEWIDE" />
 *   <ShippingMarquee messages={["Spring Sale Live", "Use code GLOW20"]} />
 *
 * Props:
 *   message:  string  (single line — easiest to swap)
 *   messages: array   (multiple lines — interleaved across the loop)
 *   icon:     element (defaults to <Truck/>; pass null to hide)
 */
const DEFAULT_MESSAGE = "FREE SHIPPING ON ORDERS OVER $150 CAD CANADA-WIDE";

const Item = ({ text, icon }) => (
    <span
        className="inline-flex items-center gap-3 text-[10px] sm:text-[11px] font-semibold tracking-[0.28em] uppercase text-white whitespace-nowrap"
        style={{ textShadow: "0 0 14px rgba(167, 139, 250, 0.55), 0 0 30px rgba(99, 102, 241, 0.35)" }}
    >
        {icon !== null && (icon ?? <Truck className="w-3.5 h-3.5 text-violet-200 shrink-0" strokeWidth={2.2} />)}
        {text}
        <span
            className="inline-block w-1 h-1 rounded-full bg-violet-300"
            style={{ boxShadow: "0 0 10px rgba(167, 139, 250, 0.95)" }}
        />
    </span>
);

export const ShippingMarquee = ({ message, messages, icon }) => {
    // Resolve final list of strings to render.
    const list =
        Array.isArray(messages) && messages.length > 0
            ? messages
            : [message || DEFAULT_MESSAGE];

    // Each "set" interleaves all messages once. Repeat the set so the track is
    // long enough on wide screens, then render the whole thing twice so the
    // -50% scroll loop is perfectly seamless.
    const setRepeats = list.length === 1 ? 6 : 3;
    const oneSet = [];
    for (let r = 0; r < setRepeats; r++) {
        list.forEach((t, i) => oneSet.push({ text: t, key: `${r}-${i}` }));
    }

    return (
        <div
            data-testid="shipping-marquee-bar"
            aria-label="Site announcement"
            className="relative w-full overflow-hidden border-b border-white/10"
            style={{
                background: "linear-gradient(90deg, #1A0B3D 0%, #2A1463 25%, #1F2A8C 55%, #1B3DB8 80%, #102060 100%)",
            }}
        >
            <span className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-violet-300/40 to-transparent" />
            <span className="pointer-events-none absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-indigo-300/35 to-transparent" />

            <div className="relative h-7 sm:h-8 flex items-center">
                <div className="shipping-marquee-track flex items-center gap-12 pl-12 will-change-transform">
                    {oneSet.map((it) => (
                        <Item key={it.key} text={it.text} icon={icon} />
                    ))}
                    {/* Duplicate set — required for a seamless -50% loop. */}
                    {oneSet.map((it) => (
                        <Item key={`d-${it.key}`} text={it.text} icon={icon} />
                    ))}
                </div>
            </div>
        </div>
    );
};

export default ShippingMarquee;
